import type { HomeworkReviewState, HomeworkState } from "@edu/shared";

/**
 * Признак проверки перевода словом и тоном (5.3
 * `docs/translate-review-design.md`). Заменяет он плашку состояния или встаёт
 * хвостом, решает `reviewPlacement` ниже, а не вызывающий.
 *
 * ⚠️ Преподавателю сервер присылает только «ждёт проверки»: возвращённая
 * проверка у него выглядит как сданная домашка. Если «fix» или «checked»
 * всё же приедут ему (старый сервер), строка ведёт себя как без признака, а не
 * показывает ученические слова преподавателю.
 *
 * ⚠️ Признак, а не число, и числом не станет: «ждёт проверки: 14» было бы
 * счётом в адрес преподавателя (5.10 CLAUDE.md). В фильтры списка он поэтому
 * не добавлен вовсе.
 *
 * Тон: зелёный у «принято», предупреждение у «есть что исправить» (ученику
 * есть что делать), без тона у «ждёт проверки».
 */
export function reviewFlag(
  review: HomeworkReviewState | undefined,
  role: "teacher" | "student",
): { text: string; tone: "ok" | "warn" | undefined } | null {
  if (review === undefined) return null;
  if (review === "awaiting") return { text: "ждёт проверки", tone: undefined };
  if (role === "teacher") return null;
  if (review === "fix") return { text: "есть что исправить", tone: "warn" };
  return { text: "принято", tone: "ok" };
}

/**
 * Признак проверки рядом с состоянием домашки: заменяет его или стоит хвостом.
 * Одно правило на строку списка и на шапку ленты ученика, чтобы они не
 * расходились (второй круг ревью 13.10.2026).
 *
 * - «есть что исправить» заменяет состояние ВСЕГДА: у ученика есть дело, и
 *   «в работе» или «просрочена» это дело спрятали бы;
 * - «ждёт проверки» и «принято» заменяют состояние только у сданной домашки.
 *   Пока сдано не всё, «принято» над недоделанной читалось бы как «всё
 *   готово», поэтому остаётся состояние;
 * - «ждёт проверки» у несданной встаёт хвостом («в работе · ждёт проверки»):
 *   преподавателю это дело, и молча прятать его нельзя. «Принято» хвостом не
 *   встаёт: дела в нём нет ни у кого.
 */
export function reviewPlacement(
  review: HomeworkReviewState | undefined | null,
  role: "teacher" | "student",
  state: HomeworkState,
): { text: string; tone: "ok" | "warn" | undefined; replaces: boolean } | null {
  const flag = reviewFlag(review ?? undefined, role);
  if (flag === null) return null;
  if (review === "fix" || state === "done") return { ...flag, replaces: true };
  if (review === "awaiting") return { ...flag, replaces: false };
  return null;
}
